export type CommunityAttachmentKind = 'image' | 'file'

export const COMMUNITY_ATTACHMENT_LIMITS = {
  maxImageBytes: 750 * 1024,
  maxFileBytes: 5 * 1024 * 1024,
} as const

const IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp'])
const FILE_MIME_TYPES = new Set([
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
])

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp'])
const FILE_EXTENSIONS = new Set(['pdf', 'txt', 'md', 'csv', 'json', 'xls', 'xlsx'])

export const COMMUNITY_ATTACHMENT_ACCEPT =
  [
    // imagens
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    // documentos e planilhas
    'application/pdf',
    'text/plain',
    'text/markdown',
    'text/csv',
    'application/json',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    // fallback por extensões (alguns browsers não preenchem file.type)
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.webp',
    '.pdf',
    '.txt',
    '.md',
    '.csv',
    '.json',
    '.xls',
    '.xlsx',
  ].join(',')

export function getFileExtension(filename: string): string {
  const name = String(filename || '').trim()
  if (!name) return ''
  const parts = name.split('.')
  if (parts.length < 2) return ''
  return String(parts.pop() || '')
    .trim()
    .toLowerCase()
}

export function isCommunityImageFile(input: { type?: string; name?: string }): boolean {
  const mime = String(input?.type || '').trim().toLowerCase()
  if (mime) return mime.startsWith('image/')
  const ext = getFileExtension(String(input?.name || ''))
  return IMAGE_EXTENSIONS.has(ext)
}

export function isCommunityAllowedFile(file: File): boolean {
  const mime = String(file?.type || '').trim().toLowerCase()
  if (mime) {
    if (mime.startsWith('image/')) return IMAGE_MIME_TYPES.has(mime)
    return FILE_MIME_TYPES.has(mime)
  }
  const ext = getFileExtension(String(file?.name || ''))
  return IMAGE_EXTENSIONS.has(ext) || FILE_EXTENSIONS.has(ext)
}

export function validateCommunityAttachmentFile(
  file: File,
): { ok: true; kind: CommunityAttachmentKind } | { ok: false; reason: string } {
  if (!file || typeof file.size !== 'number' || file.size <= 0) {
    return { ok: false, reason: 'Arquivo inválido.' }
  }

  if (!isCommunityAllowedFile(file)) {
    return { ok: false, reason: 'Tipo de arquivo não suportado.' }
  }

  const kind: CommunityAttachmentKind = isCommunityImageFile(file) ? 'image' : 'file'
  const maxBytes =
    kind === 'image'
      ? COMMUNITY_ATTACHMENT_LIMITS.maxImageBytes
      : COMMUNITY_ATTACHMENT_LIMITS.maxFileBytes

  if (file.size > maxBytes) {
    const limitLabel = kind === 'image' ? '750 KB' : '5 MB'
    return { ok: false, reason: `Limite excedido (${limitLabel} por arquivo).` }
  }

  return { ok: true, kind }
}

export function isImageAttachment(input: {
  contentType?: string | null
  url?: string | null
  storagePath?: string | null
}): boolean {
  const mime = String(input?.contentType || '').trim().toLowerCase()
  if (mime) return mime.startsWith('image/')

  const byPath = String(input?.storagePath || input?.url || '')
  const ext = getFileExtension(byPath)
  return IMAGE_EXTENSIONS.has(ext)
}

export function getAttachmentDisplayName(input: { url: string; storagePath?: string | null }): string {
  const raw = String(input.storagePath || input.url || '').trim()
  const last = raw.split('/').filter(Boolean).pop() || 'arquivo'

  // padrão do upload: <ts>-<rand>-<slug>.<ext>
  const match = last.match(/^\d+-[a-z0-9]+-(.+)$/i)
  if (match && match[1]) return match[1]
  return last
}

